import * as React from "react";
import {
  Link,
  createFileRoute,
  stripSearchParams,
  useNavigate,
} from "@tanstack/react-router";
import * as z from "zod";
import { zodValidator } from "@tanstack/zod-adapter";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { EyeIcon, PencilIcon, PlusIcon, SearchIcon, TrashIcon, XIcon } from "lucide-react";
import { HTTPError } from "ky";
import type { ColumnDef } from "@tanstack/react-table";
import type {
  Policy,
  PolicyFormData,
  PolicyType,
} from "@/lib/queries/policies";
import { useDebouncedSearch } from "@/lib/hooks";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  GenericPaginationLinks,
  GenericTable,
} from "@/components/generic-table";
import { Badge } from "@/components/ui/badge";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { fuzzyFilter } from "@/lib/table/fuzzyFilter";
import {
  POLICY_TYPES,
  policiesListQueryOptions,
  policyEntitiesQueryOptions,
  policyFormSchema,
  policyTypeSchema,
  usePoliciesList,
} from "@/lib/queries/policies";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { generateDefaultValue } from "@/lib/utils/zod";
import { api } from "@/lib/http";
import {
  parseGatewayKeyConflict,
  savePendingPolicyConflict,
} from "@/lib/policy-conflicts";
import { allEntitiesQueryOptions } from "@/lib/queries/entities";
import { Spinner } from "@/components/ui/spinner";
import { Actions } from "@/components/ui/actions";
import { useResourceAccessPermission } from "@/lib/perms";

const searchSchema = z.object({
  page: z.number().int().positive().catch(1).default(1),
  per_page: z.number().int().positive().max(100).catch(10).default(10),
  search: z.string().optional(),
  policy_type: policyTypeSchema.optional(),
  is_active: z.boolean().optional(),
  priority: z.coerce.number().min(-20).max(20).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/policies")({
  component: RouteComponent,

  validateSearch: zodValidator(searchSchema),
  search: { middlewares: [stripSearchParams({ search: "" })] },

  loaderDeps: ({ search }) => search,
  loader({ context: { queryClient }, deps }) {
    queryClient
      .ensureQueryData(policiesListQueryOptions(deps))
      .then((policies) =>
        policies.items.map((p) =>
          queryClient.ensureQueryData(policyEntitiesQueryOptions(p.id)),
        ),
      );
    queryClient.ensureQueryData(allEntitiesQueryOptions());
  },
  staticData: {
    permissionGuards: [
      {
        resource: "policies",
      },
    ],
  },
});

const columns = [
  { header: "Name", accessorKey: "name", size: 60 },
  {
    header: "Type",
    accessorKey: "type",
    size: 60,
    cell: ({ getValue }) => (
      <Badge variant="outline">{getValue<PolicyType>()}</Badge>
    ),
  },
  {
    header: "Priority",
    accessorKey: "priority",
    size: 60,
    cell: ({ getValue }) => (
      <span className="font-mono">{getValue<number>()}</span>
    ),
  },
  {
    header: "Status",
    accessorKey: "is_active",
    size: 60,
    cell: ({ getValue }) => {
      const on = getValue<boolean>();
      return (
        <StatusBadge variant={on ? "success" : "default"}>
          {on ? "Active" : "Inactive"}
        </StatusBadge>
      );
    },
  },
  {
    header: "Created By",
    accessorKey: "created_by.full_name",
    size: 60,
  },
  {
    id: "actions",
    header: "Actions",
    size: 60,
    meta: { align: "right" },
    cell: ({ row: { original } }) => {
      const canView = useResourceAccessPermission("policies", ["read"]);
      const canEdit = useResourceAccessPermission("policies", ["update"]);
      const canDelete = useResourceAccessPermission("policies", ["delete"]);

      return (
        <Actions>
          {canView.data && (
            <Button
              size="lg"
              variant="ghost"
              className="w-full justify-start text-base"
              asChild
            >
              <Link to="/policies/$id" params={{ id: original.id }}>
                <EyeIcon className="size-5" />
                View
              </Link>
            </Button>
          )}
          {canEdit.data && (
            <EditDialog asChild policy={original}>
              <Button
                size="lg"
                variant="ghost"
                className="w-full justify-start text-base"
              >
                <PencilIcon className="size-5" />
                Edit
              </Button>
            </EditDialog>
          )}
          {canDelete.data && (
            <DeleteDialog policy={original} disabled={original.is_pre_ready} />
          )}
        </Actions>
      );
    },
  },
] as const satisfies Array<ColumnDef<Policy>>;

function RouteComponent() {
  const searchParams = Route.useSearch();
  const navigate = Route.useNavigate();

  const [searchQuery, setSearchQuery, rawSearchQuery] = useDebouncedSearch(
    Route.id,
    "search",
  );

  const policies = usePoliciesList({
    ...searchParams,
    search: searchQuery,
  });

  const canView = useResourceAccessPermission("policies", ["read"]);
  const canEdit = useResourceAccessPermission("policies", ["update"]);
  const canDelete = useResourceAccessPermission("policies", ["delete"]);
  const canCreate = useResourceAccessPermission("policies", ["create"]);

  const showActions = Boolean(canView.data || canEdit.data || canDelete.data);

  const table = useReactTable({
    data: policies.data?.items ?? [],
    columns,
    filterFns: { fuzzy: fuzzyFilter },
    state: {
      pagination: {
        pageIndex: searchParams.page - 1,
        pageSize: searchParams.per_page,
      },
      columnVisibility: {
        actions: showActions,
      },
    },
    rowCount: policies.data?.count ?? 1,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableColumnResizing: false,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-3xl font-bold">Policies</h1>
        {canCreate.data && (
          <CreateDialog asChild>
            <Button>
              <PlusIcon />
              Create Policy
            </Button>
          </CreateDialog>
        )}
      </div>

      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="relative w-full sm:w-64">
          <SearchIcon className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search policies..."
            value={rawSearchQuery ?? ""}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
          <Select
            value={searchParams.policy_type ?? "ALL"}
            onValueChange={(v: PolicyType | "ALL") =>
              navigate({
                search: (old) => ({
                  ...old,
                  policy_type: v === "ALL" ? undefined : v,
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-36">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Types</SelectItem>
              {POLICY_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={
              searchParams.is_active === undefined
                ? "ALL"
                : String(searchParams.is_active)
            }
            onValueChange={(v) =>
              navigate({
                search: (old) => ({
                  ...old,
                  is_active: v === "ALL" ? undefined : v === "true",
                  page: 1,
                }),
              })
            }
          >
            <SelectTrigger className="w-full sm:w-36">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">All Statuses</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          <Input
            type="number"
            placeholder="Priority (-20 ~ 20)"
            min={-20}
            max={20}
            value={searchParams.priority ?? ""}
            onChange={(e) =>
              navigate({
                search: (old) => ({
                  ...old,
                  priority:
                    e.target.value === "" ? undefined : e.target.valueAsNumber,
                  page: 1,
                }),
              })
            }
            className="w-full sm:w-44"
          />

          <Button
            variant="outline"
            className="border-primary text-primary hover:bg-primary/10 hover:text-primary"
            onClick={() => {
              setSearchQuery("");
              navigate({
                search: () => ({ page: 1, per_page: searchParams.per_page }),
              });
            }}
          >
            Reset Filters
          </Button>
        </div>
      </div>

      <div className="rounded-md border">
        <GenericTable table={table} isLoading={policies.isLoading} />
      </div>

      <div className="mt-4">
        <GenericPaginationLinks table={table} />
      </div>
    </div>
  );
}

function DeleteDialog({
  policy,
  disabled,
}: {
  policy: Policy;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();

  const deleteMutation = useMutation({
    mutationFn: () => api.policies.deletePolicy(policy.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["policies", "list"] });
      qc.removeQueries({ queryKey: ["policies", "detail", policy.id] });

      setOpen(false);
      toast.success(`Policy "${policy.name}" deleted successfully`);
    },
  });

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="lg"
          variant="ghost"
          className="w-full justify-start text-base"
          disabled={disabled}
        >
          <TrashIcon className="size-5" />
          Delete
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete Policy</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to delete the policy "{policy.name}"? This
            action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel asChild>
            <Button variant="outline" disabled={deleteMutation.isPending}>
              Cancel
            </Button>
          </AlertDialogCancel>
          <Button
            variant="destructive"
            disabled={deleteMutation.isPending}
            onClick={() => deleteMutation.mutate()}
          >
            Delete
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function EditDialog({
  children,
  policy,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean; policy: Policy }>) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const entities = useQuery(policyEntitiesQueryOptions(policy.id));

  const updateMutation = useMutation({
    mutationFn: (data: PolicyFormData) =>
      api.policies.updatePolicy(policy.id, data),
    onSuccess(data) {
      qc.invalidateQueries({ queryKey: ["policies", "list"] });
      qc.setQueryData(["policies", "detail", policy.id], data);

      setOpen(false);
      toast.success(`Policy "${data.name}" updated successfully`);
    },
    async onError(err, variables) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        savePendingPolicyConflict({
          returnUrl: window.location.pathname + window.location.search,
          apiRequest: {
            method: "PATCH",
            url: `api/v1/policies/${policy.id}`,
            payload: variables as unknown as Record<string, unknown>,
            kind: "update-policy",
            label: variables.name,
          },
          successToast: `Policy "${variables.name}" updated successfully`,
          conflict,
        });
        setOpen(false);
        navigate({ to: "/resolve-conflicts" });
        return;
      }

      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) message = body.detail;
      }
      toast.error(`Update failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Policy</DialogTitle>
        </DialogHeader>
        <PolicyForm
          defaultValue={{
            entities: entities.data?.map((e) => e.id),
            ...policy,
          }}
          disabled={updateMutation.isPending}
          buttonText={updateMutation.isPending ? "Saving..." : "Update"}
          onSubmit={(d) => updateMutation.mutate(d)}
          onCancel={() => setOpen(false)}
          isEdit={true}
          isPreReady={policy.is_pre_ready}
        />
      </DialogContent>
    </Dialog>
  );
}

function CreateDialog({
  children,
  asChild,
}: React.PropsWithChildren<{ asChild?: boolean }>) {
  const [open, setOpen] = React.useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  const createMutation = useMutation({
    mutationFn: (data: PolicyFormData) => api.policies.createPolicy(data),
    onSuccess(data) {
      qc.invalidateQueries({ queryKey: ["policies", "list"] });
      qc.setQueryData(["policies", "detail", data.id], data);

      setOpen(false);
      toast.success(`Policy "${data.name}" created successfully`);
    },
    async onError(err, variables) {
      const conflict = await parseGatewayKeyConflict(err);
      if (conflict) {
        savePendingPolicyConflict({
          returnUrl: window.location.pathname + window.location.search,
          apiRequest: {
            method: "POST",
            url: "api/v1/policies/",
            payload: variables as unknown as Record<string, unknown>,
            kind: "create-policy",
            label: variables.name,
          },
          successToast: `Policy "${variables.name}" created successfully`,
          conflict,
        });
        setOpen(false);
        navigate({ to: "/resolve-conflicts" });
        return;
      }

      let message = err.message;
      if (err instanceof HTTPError) {
        const body = await err.response.json().catch(() => null);
        if (body?.detail) message = body.detail;
      }
      toast.error(`Create failed: ${message}`);
    },
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild={asChild}>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create Policy</DialogTitle>
        </DialogHeader>
        <PolicyForm
          disabled={createMutation.isPending}
          buttonText={createMutation.isPending ? "Saving..." : "Create"}
          onSubmit={(d) => createMutation.mutate(d)}
          onCancel={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function PolicyForm({
  disabled,
  buttonText,
  defaultValue,
  onSubmit,
  onCancel,
  // isEdit,
  isPreReady,
}: {
  disabled?: boolean;
  defaultValue?: PolicyFormData;
  buttonText: React.ReactNode;
  onSubmit: (data: PolicyFormData) => void;
  onCancel?: () => void;
  isEdit?: boolean;
  isPreReady?: boolean;
}) {
  const form = useForm({
    resolver: zodResolver(policyFormSchema),
    defaultValues: {
      ...generateDefaultValue(policyFormSchema),
      ...defaultValue,
    },
  });

  const entities = useQuery(allEntitiesQueryOptions());

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter policy name"
                  {...field}
                  disabled={isPreReady}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="priority"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Priority</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="0"
                  {...field}
                  value={String(field.value ?? 0)}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              </FormControl>
              <FormDescription>
                Priority should be between -20 and 20
              </FormDescription>

              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex pr-20">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {POLICY_TYPES.map((t) => (
                      <SelectItem key={t} value={t}>
                        {t}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="entities"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Entities</FormLabel>
              <FormControl>
                <div className="flex max-h-30 w-full flex-wrap gap-1 overflow-x-scroll rounded-lg border p-3">
                  {entities.isSuccess ? (
                    entities.data.map((e) => {
                      const enabled = field.value?.includes(e.id);
                      return (
                        <Badge
                          key={e.id}
                          className="py-0.5 transition-colors dark:border-gray-500/50"
                          variant={enabled ? "default" : "outline"}
                          onClick={() => {
                            if (isPreReady) return;
                            // field.onChange doesn't work on first click
                            const currentValue = form.getValues("entities");

                            if (enabled)
                              form.setValue(
                                field.name,
                                currentValue?.filter((old) => old !== e.id),
                              );
                            else
                              form.setValue(field.name, [
                                ...(currentValue ?? []),
                                e.id,
                              ]);
                          }}
                          style={
                            isPreReady
                              ? { cursor: "not-allowed", opacity: 0.6 }
                              : { cursor: "pointer" }
                          }
                        >
                          {e.name}
                          {!isPreReady && (enabled ? <XIcon /> : <PlusIcon />)}
                        </Badge>
                      );
                    })
                  ) : (
                    <span className="flex items-center gap-1">
                      <Spinner />
                      Loading...
                    </span>
                  )}
                </div>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {/* <FormField
          control={form.control}
          name="is_pre_ready"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Preset</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Mark as ready for production
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isEdit}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        /> */}
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
              <div className="space-y-0.5">
                <FormLabel>Active</FormLabel>
                <div className="text-sm text-muted-foreground">
                  Enable this policy
                </div>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={disabled}
            >
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={disabled}>
            {buttonText}
          </Button>
        </div>
      </form>
    </Form>
  );
}
